import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import {
    Plus,
    Clock,
    User,
    Stethoscope,
    Trash2,
    CheckCircle2,
    XCircle,
    CalendarClock,
    MessageCircle,
    MessageSquare,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
    scheduled: { label: "Planlandı", color: "bg-blue-100 text-blue-800" },
    completed: {
        label: "Tamamlandı",
        color: "bg-emerald-100 text-emerald-800",
    },
    cancelled: { label: "İptal", color: "bg-slate-200 text-slate-700" },
};

function pad(n) {
    return String(n).padStart(2, "0");
}
function localDateStr(d) {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
const dayKey = (iso) => (iso ? localDateStr(new Date(iso)) : "");
const hhmm = (iso) => {
    const d = new Date(iso);
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function Appointments() {
    const [items, setItems] = useState([]);
    const [patients, setPatients] = useState([]);
    const [treatments, setTreatments] = useState([]);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [open, setOpen] = useState(false);
    const today = new Date();
    const [form, setForm] = useState({
        patient_id: "",
        treatment_id: "",
        title: "",
        date: localDateStr(today),
        time: "10:00",
        duration: 30,
        notes: "",
    });

    async function loadAll() {
        const [a, p, t] = await Promise.all([
            api.get("/appointments"),
            api.get("/patients"),
            api.get("/treatments"),
        ]);
        setItems(a.data);
        setPatients(p.data);
        setTreatments(t.data);
    }
    useEffect(() => {
        loadAll();
    }, []);

    const patientTreatments = useMemo(
        () => treatments.filter((t) => t.patient_id === form.patient_id),
        [treatments, form.patient_id],
    );

    async function submit(e) {
        e.preventDefault();
        try {
            const start = new Date(`${form.date}T${form.time}:00`);
            const end = new Date(
                start.getTime() + Number(form.duration) * 60000,
            );
            await api.post("/appointments", {
                patient_id: form.patient_id,
                treatment_id: form.treatment_id || null,
                title: form.title || "Randevu",
                start: start.toISOString(),
                end: end.toISOString(),
                notes: form.notes,
                status: "scheduled",
            });
            toast.success("Randevu oluşturuldu");
            setOpen(false);
            setForm({
                patient_id: "",
                treatment_id: "",
                title: "",
                date: localDateStr(today),
                time: "10:00",
                duration: 30,
                notes: "",
            });
            loadAll();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    async function updateStatus(id, status) {
        try {
            await api.patch(`/appointments/${id}`, { status });
            toast.success("Durum güncellendi");
            loadAll();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }
    async function remind(id, channel) {
        try {
            const { data } = await api.post(`/appointments/${id}/remind`, { channel });
            if (data.status === "sent") toast.success("Hatırlatma gönderildi");
            else toast.error(`Gönderilemedi: ${data.error || ""} (kontür iade edildi)`);
            loadAll();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }
    async function del(id) {
        if (!window.confirm("Randevu silinsin mi?")) return;
        try {
            await api.delete(`/appointments/${id}`);
            toast.success("Randevu silindi");
            loadAll();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    // Group appointments by day for calendar markers
    const bookedDays = useMemo(() => {
        const s = new Set();
        for (const a of items) {
            if (a.start) s.add(dayKey(a.start));
        }
        return s;
    }, [items]);

    const selectedKey = localDateStr(selectedDate);
    const dayItems = useMemo(
        () =>
            items
                .filter((a) => dayKey(a.start) === selectedKey)
                .sort((a, b) => a.start.localeCompare(b.start)),
        [items, selectedKey],
    );

    const upcoming = useMemo(
        () =>
            items
                .filter(
                    (a) =>
                        a.status === "scheduled" &&
                        new Date(a.start) >= new Date(),
                )
                .slice(0, 5),
        [items],
    );

    return (
        <div data-testid="appointments-page">
            <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900">
                        Randevu Takvimi
                    </h1>
                    <p className="text-slate-600 mt-1">
                        Hastalarınızın tedavi randevularını takvim üzerinden
                        yönetin
                    </p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button
                            className="dk-btn-primary rounded-full"
                            data-testid="add-appointment-btn"
                        >
                            <Plus className="w-4 h-4 mr-1" /> Yeni Randevu
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                        <DialogHeader>
                            <DialogTitle>Yeni Randevu</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={submit} className="space-y-3">
                            <div>
                                <Label>Hasta</Label>
                                <Select
                                    value={form.patient_id}
                                    onValueChange={(v) =>
                                        setForm({
                                            ...form,
                                            patient_id: v,
                                            treatment_id: "",
                                        })
                                    }
                                >
                                    <SelectTrigger data-testid="appt-patient-select">
                                        <SelectValue placeholder="Hasta seçin" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {patients.map((p) => (
                                            <SelectItem
                                                key={p.id}
                                                value={p.id}
                                            >
                                                {p.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Tedavi (opsiyonel)</Label>
                                <Select
                                    value={form.treatment_id || "none"}
                                    onValueChange={(v) =>
                                        setForm({
                                            ...form,
                                            treatment_id:
                                                v === "none" ? "" : v,
                                            title:
                                                v === "none"
                                                    ? form.title
                                                    : patientTreatments.find(
                                                          (t) =>
                                                              t.id === v,
                                                      )?.title || form.title,
                                        })
                                    }
                                    disabled={!form.patient_id}
                                >
                                    <SelectTrigger data-testid="appt-treatment-select">
                                        <SelectValue placeholder="Tedavi seçin" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">
                                            Tedaviye bağlama
                                        </SelectItem>
                                        {patientTreatments.map((t) => (
                                            <SelectItem
                                                key={t.id}
                                                value={t.id}
                                            >
                                                {t.title}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Başlık</Label>
                                <Input
                                    required
                                    value={form.title}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            title: e.target.value,
                                        })
                                    }
                                    placeholder="Kontrol, dolgu, çekim..."
                                    data-testid="appt-title-input"
                                />
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <Label>Tarih</Label>
                                    <Input
                                        type="date"
                                        required
                                        value={form.date}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                date: e.target.value,
                                            })
                                        }
                                        data-testid="appt-date-input"
                                    />
                                </div>
                                <div>
                                    <Label>Saat</Label>
                                    <Input
                                        type="time"
                                        required
                                        value={form.time}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                time: e.target.value,
                                            })
                                        }
                                        data-testid="appt-time-input"
                                    />
                                </div>
                                <div>
                                    <Label>Süre (dk)</Label>
                                    <Input
                                        type="number"
                                        min="5"
                                        step="5"
                                        value={form.duration}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                duration: e.target.value,
                                            })
                                        }
                                    />
                                </div>
                            </div>
                            <div>
                                <Label>Notlar</Label>
                                <Textarea
                                    value={form.notes}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            notes: e.target.value,
                                        })
                                    }
                                />
                            </div>
                            <Button
                                type="submit"
                                className="dk-btn-primary w-full"
                                data-testid="appt-submit-btn"
                            >
                                Randevu Oluştur
                            </Button>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="grid lg:grid-cols-[420px_1fr] gap-6">
                <Card>
                    <CardContent className="p-3">
                        <Calendar
                            mode="single"
                            selected={selectedDate}
                            onSelect={(d) => d && setSelectedDate(d)}
                            className="rounded-lg"
                            modifiers={{
                                booked: (d) =>
                                    bookedDays.has(localDateStr(d)),
                            }}
                            modifiersClassNames={{
                                booked:
                                    "bg-teal-100 text-teal-900 font-semibold",
                            }}
                        />
                        <div className="mt-3 px-3 pb-2 text-xs text-slate-500 flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-teal-100 border border-teal-300" />
                            Randevusu olan günler
                        </div>
                        {upcoming.length > 0 && (
                            <div className="border-t pt-3 mt-2 px-2">
                                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1">
                                    <CalendarClock className="w-3.5 h-3.5" />
                                    Yaklaşan
                                </div>
                                <div className="space-y-1.5">
                                    {upcoming.map((a) => (
                                        <button
                                            key={a.id}
                                            onClick={() =>
                                                setSelectedDate(
                                                    new Date(a.start),
                                                )
                                            }
                                            className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-slate-50 transition"
                                        >
                                            <span className="font-mono text-teal-700">
                                                {dayKey(a.start).slice(5)}{" "}
                                                {hhmm(a.start)}
                                            </span>
                                            <span className="text-slate-700 ml-2">
                                                {a.patient_name}
                                            </span>
                                            <span className="text-slate-500 ml-1">
                                                — {a.title}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <div>
                    <h2 className="text-xl font-semibold text-slate-900 mb-4">
                        {selectedDate.toLocaleDateString("tr-TR", {
                            weekday: "long",
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                        })}
                    </h2>
                    {dayItems.length === 0 ? (
                        <Card>
                            <CardContent className="p-10 text-center text-slate-500">
                                Bu gün için randevu yok.
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="space-y-3" data-testid="day-appointments">
                            {dayItems.map((a) => {
                                const st = STATUS_LABEL[a.status] || STATUS_LABEL.scheduled;
                                return (
                                    <Card key={a.id}>
                                        <CardContent className="p-4">
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="flex items-start gap-3">
                                                    <div className="w-14 h-14 rounded-xl bg-teal-50 border border-teal-200 flex flex-col items-center justify-center flex-shrink-0">
                                                        <Clock className="w-4 h-4 text-teal-700" />
                                                        <span className="text-sm font-bold mono text-teal-900 mt-0.5">
                                                            {hhmm(a.start)}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <div className="font-semibold text-slate-900 flex items-center gap-2">
                                                            {a.title}
                                                            <span
                                                                className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${st.color}`}
                                                            >
                                                                {st.label}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-3 text-sm text-slate-600 mt-1">
                                                            <span className="flex items-center gap-1">
                                                                <User className="w-3.5 h-3.5" />
                                                                {a.patient_name}
                                                            </span>
                                                            {a.treatment_title && (
                                                                <span className="flex items-center gap-1">
                                                                    <Stethoscope className="w-3.5 h-3.5" />
                                                                    {a.treatment_title}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {a.notes && (
                                                            <p className="text-sm text-slate-500 mt-2">
                                                                {a.notes}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    {a.status === "scheduled" && (
                                                        <>
                                                            {a.reminder_sent_at && (
                                                                <span className="text-[10px] text-emerald-700 mr-1" data-testid={`appt-reminded-${a.id}`}>
                                                                    Hatırlatıldı
                                                                </span>
                                                            )}
                                                            <Button size="sm" variant="ghost" onClick={() => remind(a.id, "whatsapp")}
                                                                className="text-emerald-600 hover:bg-emerald-50" data-testid={`appt-remind-wa-${a.id}`}
                                                                title="WhatsApp ile hatırlat">
                                                                <MessageCircle className="w-4 h-4" />
                                                            </Button>
                                                            <Button size="sm" variant="ghost" onClick={() => remind(a.id, "sms")}
                                                                className="text-sky-600 hover:bg-sky-50" data-testid={`appt-remind-sms-${a.id}`}
                                                                title="SMS ile hatırlat">
                                                                <MessageSquare className="w-4 h-4" />
                                                            </Button>
                                                        </>
                                                    )}
                                                    {a.status !== "completed" && (
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() =>
                                                                updateStatus(
                                                                    a.id,
                                                                    "completed",
                                                                )
                                                            }
                                                            className="text-emerald-600 hover:bg-emerald-50"
                                                            data-testid={`appt-complete-${a.id}`}
                                                            title="Tamamlandı"
                                                        >
                                                            <CheckCircle2 className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                    {a.status !== "cancelled" && (
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() =>
                                                                updateStatus(
                                                                    a.id,
                                                                    "cancelled",
                                                                )
                                                            }
                                                            className="text-slate-500 hover:bg-slate-100"
                                                            title="İptal et"
                                                        >
                                                            <XCircle className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() =>
                                                            del(a.id)
                                                        }
                                                        className="text-slate-400 hover:text-rose-600"
                                                        data-testid={`appt-delete-${a.id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
